// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/checkNames.test.ts
import { type TFunction } from "i18next";

/**
 * warhol's own name for a check, and the label the rail draws for it.
 *
 * `checkProgress[].name` is warhol's vocabulary, never the person's:
 * `services/warhol/contracts/progress.py` says it is either a roster name
 * (`identifier-reader`, `evidence-verifier`, `detcheck`) or a deterministic
 * pass (`typography`, `line_diff`), and what reaches the rail is that string
 * verbatim. Drawn raw it put `barcode_zone`, `dangling_field` and
 * `identifier-reader` on the rail of a live review (Roberto, 2026-10-16) --
 * three rows of machinery where the review's own progress should be.
 *
 * The same translation-key shape `openItemKindLabel` uses for another
 * server-owned vocabulary: a known name resolves to a key under
 * `askV2.rail.checkNames`, translated in every locale. The label lives here and
 * not in warhol's payload because warhol does not know the reader's language
 * and the name is also its log and eval key; the rail is the one place that
 * turns it into words.
 *
 * The two halves of a check and its readers share a label on purpose: they are
 * one thing to the person. `identifier-reader` is the leg that reads the
 * identifiers and `identifiers` is the pass that compares them, and a rail that
 * named them differently would read as two checks.
 */
const CHECK_NAME_KEY: Record<string, string> = {
  // The deterministic passes, in `PASS_ORDER` (`tools/artwork/detcheck/passes.py`).
  typography: "typography",
  barcode_zone: "barcodeZone",
  dangling_field: "danglingField",
  line_diff: "lineDiff",
  diacritics: "diacritics",
  mandatory_copy: "mandatoryCopy",
  identifiers: "identifiers",
  correspondence: "correspondence",
  enumeration_order: "enumerationOrder",
  consistency: "consistency",
  // The readers those passes compare (`agents/artwork/readers.py`).
  "identifier-reader": "identifierReader",
  "correspondence-reader": "correspondenceReader",
  "enumeration-reader": "enumerationReader",
  "consistency-reader": "consistencyReader",
  // The other legs a fan-out settles.
  detcheck: "detcheck",
  "evidence-verifier": "evidenceVerifier",
  // The reviewers (`agents/artwork/reviewers.py`).
  "regulated-copy": "regulatedCopy",
  nutrition: "nutrition",
  "codes-values": "codesValues",
  completeness: "completeness",
  variants: "variants",
  "copy-diff": "copyDiff",
  "changed-regions": "changedRegions",
  "expert-method": "expertMethod",
  // The legs a session spawns around a review (`agents/artwork/lead.py`,
  // `agents/artwork/classifier.py`, `agents/reader.py`). `spawn.leg_entry`
  // records every settled leg, so these reach the rail too.
  "artwork-review-full": "artworkReviewFull",
  "artwork-input-classifier": "artworkInputClassifier",
  "document-reader": "documentReader",
};

/** warhol's prefix for an agent the model wrote itself (`spawn.run_custom_agent`). */
const CUSTOM_AGENT_PREFIX = "custom-";

/**
 * A check name with no house copy yet, as words.
 *
 * The floor, not the fallback: a roster name this map has not met yet still
 * has to read as English rather than as `some_new_pass`. `_` and `-` both split
 * because warhol spells deterministic passes with the first and roster names
 * with the second, and it is the same rule the thread's own `agentDisplayName`
 * applies to the same slugs, so the two surfaces word a leg alike.
 */
export function humanizeCheckName(name: string): string {
  return name
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * The rail's label for one check.
 *
 * `t` is passed in rather than read here because this module is a pure map --
 * the same shape `openItemKindLabel` takes, and the reason `checkProgressRows`
 * stays i18n-free (see its own note).
 */
export function checkRowLabel(name: string, t: TFunction<"ui">): string {
  const key = CHECK_NAME_KEY[name];
  if (key) return t(`askV2.rail.checkNames.${key}`);
  // The model names its own agents; the prefix is warhol's bookkeeping, not
  // part of the name the person should read.
  const unprefixed = name.startsWith(CUSTOM_AGENT_PREFIX)
    ? name.slice(CUSTOM_AGENT_PREFIX.length)
    : name;
  return humanizeCheckName(unprefixed || name);
}
